import React from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import Scene3D from "./Scene3D";

export default function Hero() {
  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative w-full min-h-[100svh] overflow-hidden pt-24 md:pt-28 pb-16 md:pb-20"
    >
      <div className="absolute inset-0">
        <Scene3D intensity="hero" />
      </div>
      <div
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            "radial-gradient(ellipse at center, transparent 35%, rgba(5,5,10,0.55) 70%, #05050A 100%)",
        }}
      />
      <div className="pointer-events-none absolute inset-0 scan-lines opacity-40" />

      <div className="pointer-events-none absolute left-0 top-0 h-full w-px bg-gradient-to-b from-transparent via-purple-400/40 to-transparent" />
      <div className="pointer-events-none absolute right-0 top-0 h-full w-px bg-gradient-to-b from-transparent via-amber-500/40 to-transparent" />

      {/* Side tickers — large screens only so they never collide with copy */}
      <div className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 hidden xl:block z-[1]">
        <span className="block label-tag text-purple-400 rotate-[-90deg] origin-left translate-y-12 whitespace-nowrap">
          // SECTOR 666 — TRANSMISSION 01
        </span>
      </div>
      <div className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 hidden xl:block z-[1]">
        <span className="block label-tag text-amber-400 rotate-90 origin-right -translate-y-12 whitespace-nowrap">
          NFG//SIGNAL-LOCK · 24.6X
        </span>
      </div>

      <div className="relative z-10 mx-auto max-w-7xl px-6 md:px-10 flex flex-col justify-center gap-6 md:gap-8 min-h-[calc(100svh-8rem)]">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.1 }}
        >
          <span data-testid="hero-tag" className="label-tag inline-flex items-center gap-2">
            <span className="inline-block h-2 w-2 rounded-full bg-purple-400 animate-pulse" />
            Y666.SUF // GAME STUDIO ── EST. 2025
          </span>
        </motion.div>

        <motion.h1
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.25 }}
          data-testid="hero-title"
          className="glitch font-display font-black uppercase text-[clamp(2.4rem,8vw,6.5rem)] leading-[0.95] tracking-tighter text-white max-w-5xl"
        >
          PLAY. <span className="neon-text-cyan">COMPETE</span>.
          <br />
          FOLLOW EVERY ROUND <span className="neon-text-magenta">LIVE</span>.
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.45 }}
          className="max-w-xl text-zinc-300/90 text-base md:text-lg"
        >
          NFG Games is the full live suite behind @y666.suf — Crash, Fuse, Hunt, Hangman, Push,
          Stacks, and more. Shared virtual points, chat control, and real-time leaderboards.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.6 }}
          className="flex flex-wrap items-center gap-3 md:gap-4"
        >
          <a href="/play" data-testid="hero-cta-play-web" className="btn-neon">
            ► Play NFG Crash
          </a>
          <Link to="/games/nfg-crash" data-testid="hero-cta-play" className="btn-ghost">
            Explore NFG Crash
          </Link>
          <a href="/#games" data-testid="hero-cta-arsenal" className="btn-ghost">
            ◇ View Game Suite
          </a>
          <Link to="/prints" data-testid="hero-cta-prints" className="btn-ghost">
            ◇ 3D Prints
          </Link>
        </motion.div>

        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.9, delay: 0.85 }}
          className="font-mono text-[0.65rem] uppercase tracking-[0.25em] text-zinc-500 max-w-xl"
          data-testid="hero-compliance"
        >
          ▲ Virtual points only · No cash-out · No withdrawals · No real-money gambling
        </motion.p>

        <div className="mt-auto pt-8 flex flex-wrap items-end justify-between gap-4 text-xs font-mono text-zinc-500">
          <div className="hidden md:flex flex-col gap-1">
            <span className="label-tag text-purple-400">// LIVE METRIC</span>
            <span className="text-zinc-300">
              RUNS_TODAY · <span className="neon-text-cyan">2,418</span>
            </span>
          </div>
          <div className="flex items-center gap-3">
            <span className="h-px w-14 bg-purple-400/50" />
            <span className="label-tag text-amber-400">SCROLL ↓</span>
          </div>
          <div className="hidden md:flex flex-col gap-1 text-right">
            <span className="label-tag text-amber-400">PEAK MULT</span>
            <span className="text-zinc-300">
              24.62x · <span className="neon-text-magenta">VOIDRUNNER</span>
            </span>
          </div>
        </div>
      </div>
    </section>
  );
}
